import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AdminShell } from './AdminShell';
import { adminApi, AdminUser, Paged } from '@/lib/adminApi';

type Role = 'owner' | 'admin' | 'member';

function RoleBadge({ role }: { role: Role }) {
  const styles: Record<Role, string> = {
    owner: 'bg-accent-50 text-accent-700 border-accent-200',
    admin: 'bg-ink-900 text-white border-ink-900',
    member: 'bg-ink-100 text-ink-700 border-ink-200',
  };
  return (
    <span
      className={
        'inline-flex items-center rounded-sm border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ' +
        styles[role]
      }
    >
      {role}
    </span>
  );
}

export function AdminUsersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [nextRole, setNextRole] = useState<Role>('member');

  const list = useQuery<Paged<AdminUser>>({
    queryKey: ['admin', 'users', search],
    queryFn: () => adminApi.listUsers({ search: search || undefined, limit: 100 }),
  });

  const updateRole = useMutation({
    mutationFn: (vars: { id: string; role: Role }) =>
      adminApi.updateUserRole(vars.id, vars.role),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      setEditing(null);
    },
  });

  const rows = list.data?.items ?? [];

  return (
    <AdminShell titleKey="admin.users.title">
      <div className="mb-4 flex items-center gap-3">
        <input
          type="search"
          placeholder={t('admin.users.search_placeholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-80 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        {typeof list.data?.total === 'number' && (
          <span className="text-xs text-ink-500">
            {t('admin.common.total')}: {list.data.total.toLocaleString()}
          </span>
        )}
      </div>

      {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading')}</p>}

      {!list.isLoading && rows.length === 0 && (
        <p className="text-sm text-ink-500">{t('admin.users.empty')}</p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">{t('admin.users.col_email')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.users.col_name')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.users.col_role')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.users.col_company')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.users.col_verified')}</th>
                <th className="py-2.5 px-3 font-medium">{t('admin.users.col_last_login')}</th>
                <th className="py-2.5 px-3 font-medium text-right">
                  {t('admin.common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} className="border-b border-ink-100">
                  <td className="py-2.5 px-3 font-medium text-ink-900">{u.email}</td>
                  <td className="py-2.5 px-3 text-ink-700">{u.display_name}</td>
                  <td className="py-2.5 px-3">
                    <RoleBadge role={u.role} />
                  </td>
                  <td className="py-2.5 px-3 text-ink-700">{u.company_name || '—'}</td>
                  <td className="py-2.5 px-3 text-ink-500 text-xs">
                    {u.email_verified ? '✓' : '—'}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    <button
                      onClick={() => {
                        setEditing(u);
                        setNextRole(u.role);
                      }}
                      className="text-xs text-accent-700 hover:underline"
                    >
                      {t('admin.users.change_role')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-[420px] border border-ink-200">
            <h2 className="text-base font-semibold text-ink-900 mb-1">
              {t('admin.users.change_role_for', { email: editing.email })}
            </h2>
            <p className="text-xs text-ink-500 mb-4">{t('admin.users.change_role_note')}</p>
            <div className="space-y-2">
              {(['owner', 'admin', 'member'] as Role[]).map((r) => (
                <label
                  key={r}
                  className="flex items-center gap-2 px-3 py-2 rounded-md border border-ink-200 cursor-pointer hover:bg-ink-50"
                >
                  <input
                    type="radio"
                    name="role"
                    value={r}
                    checked={nextRole === r}
                    onChange={() => setNextRole(r)}
                  />
                  <RoleBadge role={r} />
                  <span className="text-sm text-ink-700">
                    {t(`admin.users.role_help_${r}`)}
                  </span>
                </label>
              ))}
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="px-3 py-1.5 text-sm text-ink-600 hover:text-ink-900"
                disabled={updateRole.isPending}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => updateRole.mutate({ id: editing.id, role: nextRole })}
                className="px-3 py-1.5 text-sm rounded-md bg-accent-700 text-white hover:bg-accent-800"
                disabled={updateRole.isPending}
              >
                {updateRole.isPending ? t('common.loading') : t('common.save')}
              </button>
            </div>
            {updateRole.isError && (
              <p className="mt-3 text-xs text-red-600">{t('admin.error_saving')}</p>
            )}
          </div>
        </div>
      )}
    </AdminShell>
  );
}
